<template>
  <li class="profile-nav pe-0 py-0" ref="profileNav">
    <div class="media profile-media" @click="toggleDropdown">
      <div class="avatar b-r-10" :style="{ backgroundColor: avatarColor, color: textColor }">
        <span>{{ initials }}</span>
      </div>

      <div class="media-body">
        <span v-if="user_label === 'DISTRIBUTOR'">{{ business_name }}</span>

        <span v-else>
          {{ first_name }}<span v-if="last_name">&nbsp;{{last_name }}</span>
        </span>

        <br />
        <p class="mb-0 font-roboto">
          <span class="text-center small">{{ user_label }}</span>
          <i class="middle fa fa-angle-down" style="margin-left: 8px;"></i>
        </p>
      </div>
    </div>

    <ul v-if="isDropdownOpen" class="profile-dropdown">
      <li class="profile-item profile" @mouseover="changeColor('profile')" @mouseleave="resetColor('profile')">
        <router-link :to="`/profile/${formattedName}`">
          <i class="fa-solid fa-user"></i>
          <span class="menu-item">Profile</span>
        </router-link>
      </li>
      <li class="profile-item settings" @mouseover="changeColor('settings')" @mouseleave="resetColor('settings')">
        <router-link to="/settings">
          <i class="fa-solid fa-gear"></i>
          <span class="menu-item">Settings</span>
        </router-link>
      </li>
      <li class="profile-item logout" @mouseover="changeColor('logout')" @mouseleave="resetColor('logout')" :style="isLoggingOut ? 'pointer-events: none; opacity: 0.7;' : ''">
        <a @click="logout" style="display: flex; align-items: center;">
          <i v-if="!isLoggingOut" class="fa-solid fa-right-from-bracket"></i>
          <span v-else class="logout-spinner"></span>
          <span class="menu-item">{{ isLoggingOut ? 'Logging out...' : 'Log out' }}</span>
        </a>
      </li>
    </ul>
  </li>
</template>

<script>
import { webUserLogout } from '@/services/dataService';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

export default {
  name: "Profile",
  data() {
    return {
      first_name: "",
      last_name: "",
      business_name: "", // Added business_name
      user_label: "",
      avatarColor: "",
      textColor: "black",
      isDropdownOpen: false,
      isLoggingOut: false,
      token: localStorage.getItem('token_set') || '',
    };
  },
  setup() {
    const router = useRouter();
    const toast = useToast();
    return { router, toast };
  },
  computed: {
    initials() {
      if (this.user_label === 'DISTRIBUTOR') {
        if (!this.business_name) return "";
        const words = this.business_name.trim().split(/\s+/);
        if (words.length >= 2) {
          return `${words[0][0].toUpperCase()}${words[1][0].toUpperCase()}`;
        } else {
          return this.business_name.substring(0, 2).toUpperCase();
        }
      }
      const firstInitial = this.first_name ? this.first_name[0].toUpperCase() : "";
      const lastInitial = this.last_name ? this.last_name[0].toUpperCase() : "";
      return `${firstInitial}${lastInitial}`;
    },
    formattedName() {
      const firstName = localStorage.getItem('first_name') || '';
      const lastName = localStorage.getItem('last_name') || '';
      const role = localStorage.getItem('user_label') || '';
      const id = localStorage.getItem('user_id') || '';

      const username = `${firstName}-${lastName}`.toLowerCase();

      const formattedRole = role
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9\-]/g, '');

      return `${username}?role=${formattedRole}&id=${id}`;
    }
  },
  created() {
    this.first_name = localStorage.getItem("first_name") || "";

    // Logic to clean up last name (handle "null" string or empty)
    const rawLastName = localStorage.getItem("last_name");
    this.last_name = (rawLastName && rawLastName !== 'null') ? rawLastName : "";

    this.business_name = localStorage.getItem("business_name") || "";
    this.user_label = localStorage.getItem("user_label") || "";

    const storedAvatarColor = localStorage.getItem("avatarColor");
    const storedTextColor = localStorage.getItem("textColor");

    if (storedAvatarColor && storedTextColor) {
      this.avatarColor = storedAvatarColor;
      this.textColor = storedTextColor;
    } else {
      this.generateAvatarAndTextColor();
    }

    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    toggleDropdown(event) {
      event.stopPropagation();
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    handleClickOutside(event) {
      if (this.$refs.profileNav && !this.$refs.profileNav.contains(event.target)) {
        this.isDropdownOpen = false;
      }
    },
    async logout() {
      if (this.isLoggingOut) return;
      this.isLoggingOut = true;
      try {
        const mobileNumber = localStorage.getItem("mobileNumber");
        localStorage.clear();
        sessionStorage.clear();
        if (mobileNumber !== null) {
          localStorage.setItem("mobileNumber", mobileNumber);
        }

        const response = await webUserLogout(this.token);

        if(response.data.status === 'success') {
          this.$router.replace("/login");
          this.toast.success(response.data.message);
        } else if(response.data.status === 'failed') {
          this.toast.error(response.data.message);
          this.isLoggingOut = false;
        }
      } catch (e) {
        this.toast.error("An unexpected error occurred during logout.");
        console.error(e);
        this.isLoggingOut = false;
      }
    },
    changeColor(item) {
      const element = document.querySelector(`.${item}`);
      if (element) {
        const icon = element.querySelector('i');
        const text = element.querySelector('.menu-item');
        if (icon && text) {
          icon.style.color = 'black';
          text.style.color = 'black';
        }
      }
    },
    resetColor(item) {
      const element = document.querySelector(`.${item}`);
      if (element) {
        const icon = element.querySelector('i');
        const text = element.querySelector('.menu-item');
        if (icon && text) {
          icon.style.color = '#3f4551';
          text.style.color = '#3f4551';
        }
      }
    },
    generateAvatarAndTextColor() {
      const lightColor = this.getLightColor();
      this.avatarColor = lightColor;
      this.textColor = this.getDarkerColor(lightColor);
      localStorage.setItem("avatarColor", this.avatarColor);
      localStorage.setItem("textColor", this.textColor);
    },
    getLightColor() {
      const r = Math.floor(Math.random() * 56 + 200);
      const g = Math.floor(Math.random() * 56 + 200);
      const b = Math.floor(Math.random() * 56 + 200);
      return `rgb(${r}, ${g}, ${b})`;
    },
    getDarkerColor(lightColor) {
      const rgb = lightColor.match(/\d+/g);
      const r = Math.floor(rgb[0] * 0.7);
      const g = Math.floor(rgb[1] * 0.7);
      const b = Math.floor(rgb[2] * 0.7);
      return `rgb(${r}, ${g}, ${b})`;
    },
  },
};
</script>

<style scoped>
.avatar {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: lighter;
  border-radius: 50%;
  text-transform: uppercase;
  cursor: pointer;
}

.media-body {
  cursor: pointer;
  user-select: none;
}

.profile-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 5px;
  width: 150px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  list-style: none;
  padding: 10px 0;
  z-index: 1000; /* Added z-index to ensure it sits on top */
}

.profile-item {
  margin: 8px 0;
  padding: 10px;
  cursor: pointer;
}

.profile-item:hover {
  background: #f5f5f5;
  border-radius: 8px;
}

.profile-item i {
  padding-right: 10px;
  color: #3f4551;
}

.profile-item .menu-item {
  color: #3f4551;
}

.profile-item.profile:hover i, .profile-item.profile:hover .menu-item,
.profile-item.settings:hover i, .profile-item.settings:hover .menu-item,
.profile-item.logout:hover i, .profile-item.logout:hover .menu-item {
  color: black;
}

.logout-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: logout-spin 1s linear infinite;
  display: inline-block;
  margin-right: 10px;
}

@keyframes logout-spin {
  to { transform: rotate(360deg); }
}
</style>